<template>
  <div class="modal fade" id="poi-dialog" tabindex="-1" aria-labelledby="poiDialogLabel" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
<h5 class="modal-title" id="poiDialogLabel">{{ dialogTitle }}</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="handleSubmit">
            <div class="mb-3">
              <label for="poi-type" class="form-label">Type</label>
              <select v-model="poiData.type" class="form-select" id="poi-type" required>
                <option value="" disabled>Select a type...</option>
                <option v-for="(type, index) in poiTypes" :key="type.poi" :value="type.serverType()">
                  {{ type.prettyName() }}
                </option>
              </select>
            </div>

            <div class="mb-3">
              <label for="poi-title" class="form-label">Title</label>
              <input
                v-model="poiData.title"
                type="text"
                class="form-control"
                id="poi-title"
                placeholder="Enter POI name"
                required
                maxlength="255"
              >
            </div>

            <div class="mb-3">
              <label for="poi-description" class="form-label">Description</label>
              <textarea
                v-model="poiData.description"
                class="form-control"
                id="poi-description"
                rows="3"
                placeholder="Add any additional details..."
                maxlength="1000"
              ></textarea>
            </div>

            <div class="mb-3" v-if="hasMultipleDays">
              <label for="poi-day" class="form-label">Day (for multi-day hikes)</label>
              <select v-model="poiData.dayNumber" class="form-select" id="poi-day">
                <option :value="undefined">Not specific to a day</option>
                <option v-for="day in availableDays" :key="day" :value="day">
                  Day {{ day }}{{ dayInfo[day]?.name ? ': ' + dayInfo[day].name : '' }}
                </option>
              </select>
            </div>

            <!-- TODO: Unhide once mobile completes notification_distance implementation
            <div class="mb-3">
              <label for="poi-notification-distance" class="form-label">Nearby notification</label>
              <select v-model="poiData.notificationDistance" class="form-select" id="poi-notification-distance">
                <option :value="null">Disabled</option>
                <option v-for="opt in notificationDistanceOptions" :key="opt.value" :value="opt.value">
                  {{ opt.label }}
                </option>
              </select>
              <small v-if="notifiableLimitReached" class="text-danger">
                You've reached the limit of {{ NOTIFIABLE_HIGHLIGHTS_PER_HIKE_LIMIT }} notifiable highlights. You can still add this highlight without a nearby notification by selecting "Disabled".
              </small>
            </div>
            -->

            <div class="mb-3" v-if="existingImages.length > 0">
              <label class="form-label">Existing images</label>
              <div class="d-flex flex-wrap gap-2 mt-2">
                <div
                  v-for="(image, index) in existingImages"
                  :key="image.clientId"
                  class="position-relative"
                >
                  <img
                    :src="image.url"
                    class="img-thumbnail"
                    style="width: 100px; height: 100px; object-fit: cover;"
                    :class="{ 'opacity-50': image.markedForRemoval }"
                  >
                  <button
                    type="button"
                    class="btn btn-sm btn-dark position-absolute top-0 end-0"
                    style="padding: 2px 6px; font-size: 12px;"
                    @click="toggleExistingImageRemoval(index)"
                  >
                    {{ image.markedForRemoval ? 'Undo' : 'Remove' }}
                  </button>
                </div>
              </div>
              <small v-if="hasExistingMarkedForRemoval" class="text-muted d-block mt-1">
                Images marked for removal will be deleted when you save.
              </small>
            </div>

            <div class="mb-3" v-if="pendingUploadPreviews.length > 0">
              <label class="form-label">Pending uploads</label>
              <div class="d-flex flex-wrap gap-2 mt-2">
                <div
                  v-for="pending in pendingUploadPreviews"
                  :key="pending.clientId"
                  class="position-relative"
                >
                  <img
                    :src="pending.url"
                    class="img-thumbnail"
                    style="width: 100px; height: 100px; object-fit: cover;"
                  >
                  <button
                    type="button"
                    class="btn btn-sm btn-danger position-absolute top-0 end-0"
                    style="padding: 2px 6px; font-size: 12px;"
                    @click="removePendingUpload(pending.clientId)"
                  >
                    ×
                  </button>
                </div>
              </div>
            </div>

            <div class="mb-3">
              <label for="poi-images" class="form-label">Images (optional)</label>
              <input
                type="file"
                class="form-control"
                id="poi-images"
                accept="image/*"
                multiple
                @change="handleImageSelect"
                ref="imageInput"
              >
              <div v-if="selectedImages.length > 0" class="mt-2">
                <small class="text-muted">{{ selectedImages.length }} image(s) selected</small>
                <div class="d-flex flex-wrap gap-2 mt-2">
                  <div v-for="(image, index) in imagePreview" :key="index" class="position-relative">
                    <img :src="image" class="img-thumbnail" style="width: 100px; height: 100px; object-fit: cover;">
                    <button
                      type="button"
                      class="btn btn-sm btn-danger position-absolute top-0 end-0"
                      style="padding: 2px 6px; font-size: 12px;"
                      @click="removeImage(index)"
                    >
                      ×
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div class="alert alert-info" role="alert">
              <small>
                <i class="bi bi-info-circle"></i>
                Location: {{ formatCoordinates(coordinates) }}
              </small>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
          <button type="button" class="btn btn-primary" @click="handleSubmit" :disabled="!isValid || uploadingImages">
            <span v-if="uploadingImages">
              <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Uploading...
            </span>
            <span v-else>{{ submitLabel }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, PropType, watch } from 'vue';
import { useStore } from 'vuex';
import {
  NOTIFIABLE_HIGHLIGHTS_PER_HIKE_LIMIT,
  countOtherNotifiableHighlights,
  isNotifiableLimitReached as computeIsNotifiableLimitReached,
} from '../constants/notifiableHighlightLimit';
import { Modal } from 'bootstrap';
import {POIUtility} from "../../../utils/models/POI";
import { MeasurementPrefUtility } from "../../../utils/helpers/MeasurementPref";
import graphClient from "../../../utils/graphql/graph_client";
import { GenerateDirectUpload } from "../../../utils/graphql/planned-trail-graph";
import SparkMD5 from 'spark-md5';

type HighlightImageUpload = {
  signedId: string;
  clientId: string;
};

type PendingUploadPreview = {
  clientId: string;
  url: string;
};

type ExistingHighlightImage = {
  id?: string | number;
  clientId: string;
  url: string;
  markedForRemoval: boolean;
  raw: any;
};

type HikeHighlight = {
  clientId: string;
  type: string;
  title: string;
  description?: string;
  coordinates: [number, number];
  dayNumber?: number;
  notificationDistance?: number | null;
  imageSignedIds?: Array<HighlightImageUpload | string>;
  images?: Array<{
    id?: string | number;
    clientId?: string;
    file?: {
      lgUrl?: string;
      smUrl?: string;
      mdUrl?: string;
      url?: string;
    };
    _destroy?: boolean;
  }>;
  imagePreviewUrls?: string[];
};

const generateHighlightImageClientId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `highlight_image_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

export default defineComponent({
  name: 'POIDialog',
  props: {
    coordinates: {
      type: Array as PropType<[number, number]>,
      required: true
    },
    existingHighlight: {
      type: Object as PropType<HikeHighlight | null>,
      default: null
    }
  },
  setup(props) {
    const store = useStore();
    
    // Get POI types from POIUtility
    const poiTypes = POIUtility.allPOIUtils();

    const notificationDistanceOptions = computed(() => {
      const pref = MeasurementPrefUtility.preference();
      const bigUnit = pref.kilometerSymbol();
      const smallUnit = pref.meterSymbol();
      return [
        { value: 1,   label: `Within 1${bigUnit}` },
        { value: 0.5, label: `Within 500${smallUnit}` },
        { value: 0.2, label: `Within 200${smallUnit}` },
        { value: 0.1, label: `Within 100${smallUnit}` }
      ];
    });

    const poiData = ref({
      type: '',
      title: '',
      description: '',
      dayNumber: undefined as number | undefined,
      notificationDistance: null as number | null
    });

    const selectedImages = ref<File[]>([]);
    const imagePreview = ref<string[]>([]);
    const imageInput = ref<HTMLInputElement | null>(null);
    const uploadingImages = ref(false);
    const existingImages = ref<ExistingHighlightImage[]>([]);
    const pendingUploads = ref<HighlightImageUpload[]>([]);
    const pendingUploadPreviews = ref<PendingUploadPreview[]>([]);

    const isEditMode = computed(() => Boolean(props.existingHighlight?.clientId));

    const normaliseUploads = (uploads?: Array<HighlightImageUpload | string>) => {
      if (!uploads || uploads.length === 0) {
        return [] as HighlightImageUpload[];
      }

      return uploads
        .map((entry) => {
          if (!entry) return null;

          if (typeof entry === 'string') {
            return {
              signedId: entry,
              clientId: generateHighlightImageClientId()
            };
          }

          const signedId = entry.signedId;
          if (!signedId) {
            return null;
          }

          return {
            signedId,
            clientId: entry.clientId || generateHighlightImageClientId()
          };
        })
        .filter(Boolean) as HighlightImageUpload[];
    };

    const initialiseForm = () => {
      if (isEditMode.value && props.existingHighlight) {
        poiData.value = {
          type: props.existingHighlight.type || '',
          title: props.existingHighlight.title || '',
          description: props.existingHighlight.description || '',
          dayNumber: props.existingHighlight.dayNumber,
          notificationDistance: props.existingHighlight.notificationDistance ?? null
        };
      } else {
        poiData.value = {
          type: '',
          title: '',
          description: '',
          dayNumber: undefined,
          notificationDistance: null
        };
      }

      selectedImages.value = [];
      imagePreview.value = [];
      if (imageInput.value) {
        imageInput.value.value = '';
      }

      if (props.existingHighlight) {
        const highlightImages = props.existingHighlight.images || [];
        existingImages.value = highlightImages
          .map((image: any) => {
            if (!image) return null;
            const clientId = image.clientId || image.client_id || generateHighlightImageClientId();
            const url = image.file?.lgUrl || image.file?.mdUrl || image.file?.url || image.url || '';
            if (!url) {
              return null;
            }

            return {
              id: image.id,
              clientId,
              url,
              markedForRemoval: Boolean(image._destroy),
              raw: {
                ...image,
                clientId
              }
            } as ExistingHighlightImage;
          })
          .filter(Boolean) as ExistingHighlightImage[];

        const normalisedExistingUploads = normaliseUploads(props.existingHighlight.imageSignedIds);
        pendingUploads.value = [...normalisedExistingUploads];

        const previewUrls = props.existingHighlight.imagePreviewUrls || [];
        pendingUploadPreviews.value = normalisedExistingUploads
          .map((upload, index) => ({
            clientId: upload.clientId,
            url: previewUrls[index] || ''
          }))
          .filter((entry) => entry.url);
      } else {
        existingImages.value = [];
        pendingUploads.value = [];
        pendingUploadPreviews.value = [];
      }
    };

    watch(() => props.existingHighlight, () => {
      initialiseForm();
    }, { immediate: true });

    const dayInfo = computed(() => store.state.planTrail.dayInfo || {});
    const routes = computed(() => store.state.planTrail.routes || {});

    const hasMultipleDays = computed(() => {
      const routeValues = Object.values(routes.value);
      const uniqueDays = new Set(routeValues.map((r: any) => r.day).filter(d => d != null));
      return uniqueDays.size > 1;
    });

    const availableDays = computed(() => {
      const routeValues = Object.values(routes.value);
      const days = new Set(routeValues.map((r: any) => r.day).filter(d => d != null));
      return Array.from(days).sort((a, b) => a - b);
    });

    const dialogTitle = computed(() => isEditMode.value ? 'Edit Hike Highlight' : 'Add Hike Highlight');
    const submitLabel = computed(() => isEditMode.value ? 'Save changes' : 'Add POI');

    const notifiableHighlightsCount = computed(() =>
      countOtherNotifiableHighlights(
        store.state.planTrail.hikeHighlights || [],
        props.existingHighlight?.clientId,
      )
    );

    const notifiableLimitReached = computed(() =>
      computeIsNotifiableLimitReached(
        poiData.value.notificationDistance != null,
        store.state.planTrail.hikeHighlights || [],
        props.existingHighlight?.clientId,
      )
    );

    const isValid = computed(() => {
      return poiData.value.type && poiData.value.title.trim().length > 0 && !notifiableLimitReached.value;
    });

    const formatCoordinates = (coords?: [number, number]) => {
      if (!coords) return '—';
      return `${coords[1].toFixed(6)}, ${coords[0].toFixed(6)}`;
    };

    const calculateMD5Base64 = async (buffer: ArrayBuffer): Promise<string> => {
      const spark = new SparkMD5.ArrayBuffer();
      spark.append(buffer);
      const hash = spark.end();
      const bytes = new Uint8Array(hash.match(/.{2}/g).map(byte => parseInt(byte, 16)));
      return btoa(String.fromCharCode(...bytes));
    };

    const handleImageSelect = (event: Event) => {
      const target = event.target as HTMLInputElement;
      const files = target.files;
      if (!files) return;

      // Clear previous selections
      selectedImages.value = [];
      imagePreview.value = [];

      // Process new files
      Array.from(files).forEach(file => {
        selectedImages.value.push(file);
        
        // Create preview URL
        const reader = new FileReader();
        reader.onload = (e) => {
          if (e.target?.result) {
            imagePreview.value.push(e.target.result as string);
          }
        };
        reader.readAsDataURL(file);
      });
    };

    const removeImage = (index: number) => {
      selectedImages.value.splice(index, 1);
      imagePreview.value.splice(index, 1);
      
      // Clear the input if no images left
      if (selectedImages.value.length === 0 && imageInput.value) {
        imageInput.value.value = '';
      }
    };

    const toggleExistingImageRemoval = (index: number) => {
      const image = existingImages.value[index];
      if (!image) return;
      existingImages.value.splice(index, 1, {
        ...image,
        markedForRemoval: !image.markedForRemoval
      });
    };

    const removePendingUpload = (clientId: string) => {
      pendingUploads.value = pendingUploads.value.filter(upload => upload.clientId !== clientId);
      pendingUploadPreviews.value = pendingUploadPreviews.value.filter(preview => preview.clientId !== clientId);
    };

    const hasExistingMarkedForRemoval = computed(() => existingImages.value.some(image => image.markedForRemoval));

    const uploadImages = async (): Promise<HighlightImageUpload[]> => {
      const uploads: HighlightImageUpload[] = [];
      
      for (const file of selectedImages.value) {
        try {
          // Calculate MD5 checksum
          const arrayBuffer = await file.arrayBuffer();
          const hashBase64 = await calculateMD5Base64(arrayBuffer);

          // Step 1: Generate direct upload credentials
          const response = await graphClient.request(GenerateDirectUpload, {
            input: {
              filename: file.name,
              byteSize: file.size,
              contentType: file.type || 'image/jpeg',
              checksumBase64: hashBase64,
              serviceName: window.publicBucketService
            }
          });

          const { uploadUrl, headers, signedId } = response.generateDirectUpload;

          // Step 2: Upload file directly to storage
          const uploadHeaders = JSON.parse(headers);
          const uploadResponse = await fetch(uploadUrl, {
            method: 'PUT',
            headers: {
              ...uploadHeaders,
              'Content-Type': file.type || 'image/jpeg'
            },
            body: file
          });

          if (!uploadResponse.ok) {
            const errorText = await uploadResponse.text();
            console.error('Upload failed:', uploadResponse.status, errorText);
            throw new Error(`Upload failed: ${uploadResponse.statusText}`);
          }

          uploads.push({
            signedId,
            clientId: generateHighlightImageClientId()
          });
        } catch (error) {
          console.error('Error uploading image:', error);
          throw error;
        }
      }
      
      return uploads;
    };

    const handleSubmit = async () => {
      if (!isValid.value) return;

      uploadingImages.value = true;

      try {
        const trimmedTitle = poiData.value.title.trim();
        const trimmedDescription = poiData.value.description.trim();

        // Upload images if any
        let uploadedImageUploads: HighlightImageUpload[] = [];
        if (selectedImages.value.length > 0) {
          uploadedImageUploads = await uploadImages();
        }

        if (isEditMode.value && props.existingHighlight) {
          const updates: Partial<HikeHighlight> = {
            type: poiData.value.type,
            title: trimmedTitle,
            description: trimmedDescription,
            coordinates: props.coordinates,
            dayNumber: poiData.value.dayNumber,
            notificationDistance: poiData.value.notificationDistance
          };

          const combinedExistingUploads = [...pendingUploads.value];
          const combinedExistingPreviews = [...pendingUploadPreviews.value];

          if (uploadedImageUploads.length > 0) {
            uploadedImageUploads.forEach((upload, index) => {
              combinedExistingUploads.push(upload);
              combinedExistingPreviews.push({
                clientId: upload.clientId,
                url: imagePreview.value[index] || ''
              });
            });
          }

          updates.imageSignedIds = combinedExistingUploads;
          updates.imagePreviewUrls = combinedExistingPreviews.map(entry => entry.url);
          updates.images = existingImages.value.map(image => ({
            ...image.raw,
            clientId: image.clientId,
            _destroy: image.markedForRemoval || undefined
          }));

          store.commit('planTrail/updateHikeHighlight', {
            clientId: props.existingHighlight.clientId,
            updates
          });

          pendingUploads.value = combinedExistingUploads;
          pendingUploadPreviews.value = combinedExistingPreviews;
        } else {
          const newPreviewEntries = uploadedImageUploads.map((upload, index) => imagePreview.value[index] || '');
          store.commit('planTrail/addHikeHighlight', {
            type: poiData.value.type,
            title: trimmedTitle,
            description: trimmedDescription,
            coordinates: props.coordinates,
            dayNumber: poiData.value.dayNumber,
            notificationDistance: poiData.value.notificationDistance,
            imageSignedIds: uploadedImageUploads.length > 0 ? uploadedImageUploads : undefined,
            imagePreviewUrls: newPreviewEntries.length > 0 ? newPreviewEntries : undefined,
            images: []
          });
        }

        await store.dispatch('planTrail/refreshHighlights');

        if (!isEditMode.value) {
          initialiseForm();
        } else {
          selectedImages.value = [];
          imagePreview.value = [];
          if (imageInput.value) {
            imageInput.value.value = '';
          }
        }

        const modalElement = document.getElementById('poi-dialog');
        if (modalElement) {
          const modalInstance = Modal.getInstance(modalElement) || new Modal(modalElement);
          modalInstance?.hide();
        }
      } catch (error) {
        console.error('Error submitting POI:', error);
        alert('Failed to upload images. Please try again.');
      } finally {
        uploadingImages.value = false;
      }
    };

    return {
      poiTypes,
      poiData,
      notificationDistanceOptions,
      dayInfo,
      hasMultipleDays,
      availableDays,
      dialogTitle,
      submitLabel,
      notifiableLimitReached,
      NOTIFIABLE_HIGHLIGHTS_PER_HIKE_LIMIT,
      isValid,
      formatCoordinates,
      handleSubmit,
      selectedImages,
      imagePreview,
      imageInput,
      uploadingImages,
      handleImageSelect,
      removeImage,
      existingImages,
      pendingUploadPreviews,
      toggleExistingImageRemoval,
      removePendingUpload,
      hasExistingMarkedForRemoval
    };
  }
});
</script>
